import {
    Component,
    Prop,
    Host,
    h,
    State,
    Listen,
    Element,
    Watch,
} from "@stencil/core";
import api from "../../utils/api";
import { debounce } from "../../utils/debounce";
import { isCookieConsentAccepted } from "../../utils/cookieConsent";
import { setCookie } from "../../utils/setCookie";

interface LocaleSelectionBannerResponse {
    availableLocales?: AvailableLocale[];
    showLocaleSelectionBanner?: boolean;
    descriptionText: string;
    buttonText: string;
    activeLocale: string;
}

interface AvailableLocale {
    localeLabel: string;
    languageISOCode: string;
    localeURL: string;
    localeValue: string;
    preselected?: boolean;
    uid: string;
}

@Component({
    tag: "ux-iws-locale-selection-banner",
    styleUrl: "locale-selection-banner.scss",
})
export class LocaleSelectionBanner {
    private baseClass = "ux-iws-locale-selection-banner";
    private selectElement: HTMLUxSelectElement | null = null;

    @Element() element: HTMLUxIwsLocaleSelectionBannerElement;

    /** the API endpoint from which the response is to await */
    @Prop() url: string = undefined;

    @State() shouldOpen: boolean = false;
    /** cumulative layout shift guard: will be deactivated 3 seconds after page load */
    @State() clsGuardActive = true;
    @State() localesData: LocaleSelectionBannerResponse | null = null;
    @State() elementHeight: number = 0;

    async componentDidLoad() {
        this.getElementHeight();
        try {
            const localesData = await api<LocaleSelectionBannerResponse>(
                this.url
            );
            this.localesData = localesData;
            this.shouldOpen = localesData.showLocaleSelectionBanner === true;
        } catch (e) {
            this.shouldOpen = false;
        }
    }

    private getSelect = (): HTMLSelectElement | null => {
        if (!this.selectElement) return null;
        return this.selectElement.querySelector("select");
    };

    private getSelectedLocale = (
        selectedLocaleValue: string
    ): AvailableLocale => {
        return this.localesData.availableLocales.find(
            (locale: AvailableLocale) => {
                return locale.localeValue === selectedLocaleValue;
            }
        );
    };

    private changeLocale = async () => {
        const selectElement = this.getSelect();
        if (selectElement === null) return;
        const selectedLocaleValue = selectElement.value;
        const selectedUrl = this.getSelectedLocale(selectedLocaleValue)
            ?.localeURL;
        const selectedUid = this.getSelectedLocale(selectedLocaleValue)?.uid;

        if (selectedUrl === undefined || selectedUid === undefined) {
            this.shouldOpen = false;
            return;
        }

        if (this.localesData.activeLocale !== selectedLocaleValue) {
            let cookieExpires = null;
            isCookieConsentAccepted()
                .then(() => {
                    /**
                     *   TODO: this imiplementation of the cookie is
                     *   not optimal, we do not set the language ISO code,
                     *   but an internal identification number.
                     *   Check with backend team if this can be changed.
                     */
                    cookieExpires = 180 * 24 * 60 * 60 * 1000;
                })
                .catch(() => {})
                .finally(() => {
                    setCookie({
                        expiresIn: cookieExpires,
                        mainBody: `language_iso=${selectedUid};`,
                    });
                    window.location.href = selectedUrl;
                });
        }
    };

    @Listen("DOMContentLoaded", { target: "window" })
    async setClsGuardOff() {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        this.clsGuardActive = false;
    }

    @Watch("localesData")
    @Watch("clsGuardActive")
    @Listen("resize", { target: "window" })
    onResize() {
        this.updateHeight();
    }

    private updateHeight = debounce(() => this.getElementHeight(), 100);

    private getElementHeight = () => {
        this.elementHeight = !this.element.firstElementChild
            ? 0
            : this.element.firstElementChild.scrollHeight;
    };

    render() {
        const readyToOpen =
            this.shouldOpen === true && this.clsGuardActive === false;

        return (
            <Host
                class={this.baseClass}
                style={{
                    height: readyToOpen ? `${this.elementHeight}px` : "0",
                }}
                data-testid="locale-selection-banner"
            >
                {this.localesData?.showLocaleSelectionBanner && (
                    <div class={`${this.baseClass}__content-container`}>
                        <div class={`${this.baseClass}__description-text`}>
                            {this.localesData.descriptionText}
                        </div>
                        <div class={`${this.baseClass}__select-container`}>
                            {this.localesData && (
                                <ux-select
                                    ref={(el) => (this.selectElement = el)}
                                    data-active-locale={
                                        this.localesData.activeLocale
                                    }
                                    data-testid="locale-selection-banner-select"
                                >
                                    {this.localesData.availableLocales.map(
                                        (locale: AvailableLocale) => (
                                            <option
                                                value={locale.localeValue}
                                                key={locale.uid}
                                                selected={
                                                    locale.preselected
                                                        ? true
                                                        : null
                                                }
                                                data-testid={locale.localeValue}
                                            >
                                                {locale.localeLabel}
                                            </option>
                                        )
                                    )}
                                </ux-select>
                            )}
                        </div>
                        <div class={`${this.baseClass}__button-container`}>
                            <ux-button
                                variant="primary"
                                onClick={this.changeLocale}
                                data-testid="change-locale"
                            >
                                {this.localesData.buttonText}
                            </ux-button>
                        </div>
                        <button
                            class={`${this.baseClass}__close-container`}
                            onClick={() => (this.shouldOpen = false)}
                            type="button"
                            data-testid="close-locale-selection-banner"
                        >
                            <ux-icon name="close"></ux-icon>
                        </button>
                    </div>
                )}
            </Host>
        );
    }
}
